import React from 'react';
import {
    Dialog,
    DialogHeader,
    DialogContent,
    DialogFooter,
    Button,
    DialogBody,
    ButtonSet,
    ButtonLink,
} from '@indeed/ifl-components';
import GettextFactory from '../../util/GettextFactory';
import { useInitialState } from '../../lib/context/InitialStateContext';

export interface ErrorDialogProps {
    /** Whether the dialog is open */
    isOpen: boolean;
    /** Callback when dialog is closed/dismissed */
    onOpenChange: (open: boolean) => void;
    /** Callback for action button */
    onAction: () => void;
    /** Optional custom error message to display */
    errorMessage?: string;
}

/**
 * Reusable error dialog component using IFL Dialog
 * Displays error messages with consistent styling and behavior
 */
export const ErrorDialog: React.FC<ErrorDialogProps> = ({
    isOpen,
    onOpenChange,
    onAction,
    errorMessage,
}) => {
    const gt = GettextFactory();
    const { returnUrl } = useInitialState();

    const defaultMessage = gt.gettext(
        'Our systems are having some trouble. You can try again to continue or finish setting up later.',
    );

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange} size="sm" portaled>
            <DialogContent>
                <DialogHeader labelClose={gt.gettext('Close')}>
                    {gt.gettext('Something went wrong')}
                </DialogHeader>
                <DialogBody>{errorMessage || defaultMessage}</DialogBody>

                <DialogFooter>
                    <ButtonSet>
                        <ButtonLink variant="secondary" href={returnUrl}>
                            {gt.gettext('Close and exit')}
                        </ButtonLink>
                        <Button
                            variant="primary"
                            onClick={() => {
                                onAction();
                                onOpenChange(false);
                            }}
                        >
                            {gt.gettext('Try again')}
                        </Button>
                    </ButtonSet>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
